import {
  RAMP_RISE_STEPS,
  type TerrainSampler,
  TILE_SHAPE_RAMP,
  TILE_SHAPE_STAIR_UP,
  type TilePos,
} from "../world/types";
import type { Aabb } from "./collision";

/*
 * Rising ground, as a body walks it: the height of the surface the mesher drew
 * inside a tile that is not a box.
 *
 * Two shipped shapes are not boxes — a **ramp**, which the land makes, and a
 * **flight of stairs**, which a town does — so they are the two shapes the AABB
 * sweep cannot see. DF's attribute table gives them the same row: walkable,
 * `walkable_up`, not `passable_low`, and *not* solid
 * (`docs/research/df-systems-map/systems/map-and-blocks.md` §5.3, transcribed in
 * `sim_core::map::TileShape`). That row is exactly right for a fluid and exactly
 * useless for a collision box: with only `isSolid` to go on, a player walks
 * through the slope on the screen and meets the hidden ledge behind it.
 *
 * So this module answers the one extra question rising ground raises — **how
 * high is the ground inside this tile** — and it answers it by transcribing the
 * geometry `crates/mesher/src/ramp.rs` emits, because collision that disagrees
 * with the picture is worse than no collision at all:
 *
 * - The mesher's slope frame puts a ramp's low edge on its own tile corner and
 *   rises one tile across and one tile up towards the wall (`slope_frame`), so
 *   the surface is the plane `z + t`, where `t` is how far across the tile the
 *   point sits along the rise.
 * - Which direction that is comes from the simulation
 *   (`TerrainSampler.rampRise`), which transcribes the *same* first-wall rule the
 *   mesher resolves it with. Nothing here derives it.
 * - A ramp with no wall to lean on is drawn as a flat plate at the bottom of its
 *   tile, and is walked as one.
 *
 * # A box on a 45-degree slope stands on its uphill edge
 *
 * The support this module reports is the *highest* point of the slope under the
 * player's footprint rather than the height under their centre, and that is
 * geometry rather than caution: a box that cannot tilt rests on whichever of its
 * bottom edges is highest, and letting it settle any lower would put a corner of
 * it inside the hill. Two things follow that are worth expecting — the feet sit
 * up to a third of a tile above the surface under the eyes while the player is
 * on a slope, and the top of a ramp is reached with the box already clear of the
 * wall the ramp leans on, which is what lets a player walk onto the terrace
 * instead of into it.
 *
 * # A flight is walked as the line through its treads
 *
 * The stair came second and it came from the town: worldgen's grading pass puts
 * a `StairUp` on every one-level step between two built surfaces
 * (`sim_core::worldgen`, wave 1.6), and until chunk St this module answered
 * `null` for one. A street that climbed in steps was a street the player walked
 * *through* — *"are the stairs real blocks? They currently have no collision"*
 * (owner, 2026-08-30). They are real, and this is where the client agrees.
 *
 * A flight is not a plane, though. The mesher draws it as two nested boxes, a
 * pair of treads half a tile apart (`crates/mesher/src/steps.rs`), so there are
 * two honest surfaces to walk and this module walks **the continuous one**: the
 * same `rampSurfaceZ` plane through the same `rampRise` direction, which is the
 * diagonal touching the outer edge of both treads. A foot placed by it is
 * therefore never above the tread it is drawn on, and never more than one course
 * — half a tile — below that tread's top.
 *
 * Three reasons, in the order that decided it (`docs/decisions/witness.md`,
 * chunk St):
 *
 * - **It is already the shipped rule for feet.** The ambient crowd has read a
 *   flight through this very call since wave 1.5 (`witness/crowd.ts`'s
 *   `surfaceZ`), and a client where a walker's feet and the player's feet
 *   disagree about one tile is a client with two grounds. Quantising here would
 *   have made the crowd wrong instead of making the player right.
 * - **The mesher's step count would have to live twice.** How many micro-steps a
 *   flight is drawn in is a taste number marked PROVISIONAL in the mesher, and
 *   nothing across the wasm boundary reports it — so a quantised surface would
 *   need a copy of it here with no startup check to catch the drift, which is
 *   the one thing every other number this client transcribes has
 *   (`world/types.ts`).
 * - **The shipped step limit forbids it, and that was measured.** A quantised
 *   surface puts a half-tile riser in the middle of every stair tile, and
 *   `collision.ts` refuses a sideways move that would hoist the box further than
 *   `MAX_SLOPE_STEP` — a quarter tile. The walk up a flight stalls dead at the
 *   riser: the shape becomes *less* walkable than it was broken. Raising that
 *   limit to let a flight through would turn the cap at the end of every ramp
 *   run into a half-tile lift, which is the glitch the limit exists to prevent.
 */

/**
 * The height of the ground inside a rising tile, under a point of the world.
 *
 * `tile.z + t` where `t` runs from 0 at the tile's low edge to 1 at the wall —
 * the mesher's `v_step`, which is "one tile towards the wall and one tile up"
 * (`crates/mesher/src/ramp.rs`).
 *
 * **A flight of stairs is read through it too**, by this module and by the crowd
 * alike, and the plane it answers with is the line through the treads rather
 * than the treads themselves (this module's header). Ramp arithmetic for a
 * staircase is the decision, not an oversight.
 *
 * **The sample is clamped into the tile it belongs to**, which is what makes the
 * awkward cases answerable at all: the point handed in is a corner of a box that
 * straddles tiles, so on a diagonal approach or at a corner it lies outside the
 * ramp entirely, and the honest reading of "the slope, over there" is the near
 * or far edge of the slope itself. A ramp is never taller than its own tile.
 *
 * `rise` is an index into {@link RAMP_RISE_STEPS}; anything else — `-1`, and any
 * id a later build might add — returns the tile's own floor, which is where the
 * mesher puts the plate of a ramp it could not lean on anything.
 */
export function rampSurfaceZ(tile: TilePos, rise: number, x: number, y: number): number {
  const step = RAMP_RISE_STEPS[rise];

  if (step === undefined) {
    return tile.z;
  }

  const [dx, dy] = step;
  // Exactly one of the two is non-zero and it is ±1 ({@link RAMP_RISE_STEPS}),
  // so one dot product picks the axis and its sign turns the tile round: a rise
  // towards -x measures from the tile's +x edge, which is where its low edge is.
  const across = (x - tile.x) * dx + (y - tile.y) * dy;

  return tile.z + clamp01(dx + dy > 0 ? across : 1 + across);
}

/**
 * Returns the highest ramp surface under a box's footprint, or `null` when the
 * box is over no ramp at all.
 *
 * Two z-levels are considered and no more: the tile the feet are in, and the one
 * directly below them. That is the whole of the reach rising ground has — a ramp
 * is one tile tall and so is a flight, which climbs one graded step
 * (`sim_core::worldgen`) — and the narrowness is a guard rather than an
 * optimisation. A player standing in a chamber *under* a ramp must not be
 * plucked up onto it, and a player falling towards one must be caught by the
 * slope under their feet rather than by one three levels down.
 */
export function slopeSupport(box: Aabb, terrain: TerrainSampler): number | null {
  // Half-open tiles, the same reading `collision.ts` takes: a box whose max
  // lands exactly on a boundary touches the next tile without overlapping it.
  const minTileX = Math.floor(box.minX);
  const maxTileX = Math.ceil(box.maxX) - 1;
  const minTileY = Math.floor(box.minY);
  const maxTileY = Math.ceil(box.maxY) - 1;
  const feet = Math.floor(box.minZ);

  let support: number | null = null;

  for (let x = minTileX; x <= maxTileX; x += 1) {
    for (let y = minTileY; y <= maxTileY; y += 1) {
      for (let z = feet - 1; z <= feet; z += 1) {
        const surface = tileSupport(box, { x, y, z }, terrain);

        if (surface !== null && (support === null || surface > support)) {
          support = surface;
        }
      }
    }
  }

  return support;
}

/**
 * Returns what one tile holds the box up at, or `null` if its ground does not
 * rise.
 *
 * The shape question is asked first and the direction second, so a tile that is
 * neither a ramp nor a flight — which is nearly every tile a player walks over —
 * costs one call across the wasm boundary rather than two.
 *
 * The slope is sampled at the box's **uphill** corner, clamped into the tile by
 * {@link rampSurfaceZ}: the maximum of a plane over a rectangle is at a corner,
 * so one sample per tile is exact rather than approximate.
 */
function tileSupport(box: Aabb, tile: TilePos, terrain: TerrainSampler): number | null {
  if (!rises(terrain.tileShape(tile.x, tile.y, tile.z))) {
    return null;
  }

  const rise = terrain.rampRise(tile.x, tile.y, tile.z);
  const [dx, dy] = RAMP_RISE_STEPS[rise] ?? [0, 0];

  return rampSurfaceZ(
    tile,
    rise,
    dx > 0 ? box.maxX : box.minX,
    dy > 0 ? box.maxY : box.minY,
  );
}

/**
 * True for the shapes whose ground climbs across their own tile.
 *
 * Two, and the rule behind them is the simulation's rather than this module's: a
 * surface climbs when the mesher draws it climbing, and the mesher's own test is
 * *"`basic == Ramp`, or `basic == Stair` and `walkable_up`"*
 * (`crates/mesher/src/coverage.rs`). A stair that only goes **down** fails it —
 * it is the head of a flight, the hole is the stair, and nothing stands on one
 * to climb — and so does every id a later build might mint, which is right:
 * an unknown shape is a box `isSolid` has already had its say about.
 *
 * **One shipped shape passes that test and is not named here**: `STAIR_UPDOWN`,
 * a shaft's step, which the mesher would draw treads for and `rampRise` would
 * answer a direction for. Nothing in the world builds one yet — worldgen grades
 * `StairUp`/`StairDown` pairs and the dig verb only ever takes one away
 * (`sim_core::dig`) — and the client has no word for it at all
 * (`world/types.ts` names the climbing stair and stops). The day something
 * builds a shaft, the vocabulary, this list and `witness/crowd.ts`'s copy of it
 * grow in the same breath; until then the two footing rules are wrong about the
 * same nothing, which is the property worth keeping.
 */
function rises(shape: number): boolean {
  return shape === TILE_SHAPE_RAMP || shape === TILE_SHAPE_STAIR_UP;
}

/** Clamps a fraction into 0..1. */
function clamp01(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}
